import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Breadcrumb = ({ customItems = null }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Route mapping for breadcrumb generation
  const routeMap = {
    '/dashboard': { label: 'Dashboard', icon: 'LayoutDashboard' },
    '/student-profile-management': { label: 'Student Profiles', icon: 'UserCircle', parent: 'Students' },
    '/iep-goals-and-objectives-tracking': { label: 'IEP Goals & Objectives', icon: 'Target', parent: 'Students' },
    '/behavioral-incident-logging': { label: 'Behavioral Incidents', icon: 'AlertTriangle', parent: 'Students' },
    '/reports-and-analytics': { label: 'Reports & Analytics', icon: 'FileText', parent: 'Analytics' },
    '/ai-powered-insights-and-recommendations': { label: 'AI Insights', icon: 'Brain', parent: 'Analytics' },
    '/multi-user-collaboration-hub': { label: 'Team Hub', icon: 'Users', parent: 'Collaboration' }
  };

  const generateBreadcrumbs = () => {
    if (customItems) return customItems;

    const pathSegments = location?.pathname?.split('/')?.filter(Boolean);
    const breadcrumbs = [];

    // Always start with Dashboard for non-dashboard routes
    if (location?.pathname !== '/dashboard') {
      breadcrumbs?.push({
        label: 'Dashboard',
        path: '/dashboard',
        icon: 'LayoutDashboard'
      });
    }

    // Build breadcrumbs based on current route
    const currentRoute = routeMap?.[location?.pathname];
    if (currentRoute) {
      // Add parent section if exists
      if (currentRoute?.parent) {
        breadcrumbs?.push({
          label: currentRoute?.parent,
          path: null, // Section headers are not clickable
          icon: null
        });
      }

      // Add current page
      breadcrumbs?.push({
        label: currentRoute?.label,
        path: location?.pathname,
        icon: currentRoute?.icon,
        current: true
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't render breadcrumbs on login page or if only one item
  if (location?.pathname === '/login' || breadcrumbs?.length <= 1) {
    return null;
  }

  const handleNavigation = (path) => {
    if (path && path !== location?.pathname) {
      navigate(path);
    }
  };

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1">
        {breadcrumbs?.map((item, index) => {
          const isLast = index === breadcrumbs?.length - 1;
          const isClickable = item?.path && !item?.current;

          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <Icon 
                  name="ChevronRight" 
                  size={14} 
                  className="mx-2 text-muted-foreground/60" 
                />
              )}
              <div className="flex items-center gap-1.5">
                {item?.icon && (
                  <Icon 
                    name={item?.icon} 
                    size={14} 
                    className={isLast ? 'text-foreground' : 'text-muted-foreground'} 
                  />
                )}
                
                {isClickable ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigation(item?.path)}
                    className="h-auto p-0 font-normal text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    {item?.label}
                  </Button>
                ) : (
                  <span 
                    className={`font-medium ${
                      isLast 
                        ? 'text-foreground' 
                        : 'text-muted-foreground'
                    }`}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item?.label}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;